import {
  NgModule,
  ApplicationRef,
  ErrorHandler,
  LOCALE_ID,
  inject,
  provideAppInitializer,
} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {FormsModule} from '@angular/forms';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatButtonModule} from '@angular/material/button';
import {MatButtonToggleModule} from '@angular/material/button-toggle';
import {MatCardModule} from '@angular/material/card';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatChipsModule} from '@angular/material/chips';
import {MatNativeDateModule, MatRippleModule} from '@angular/material/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatDialogModule} from '@angular/material/dialog';
import {MatExpansionModule} from '@angular/material/expansion';
import {MatGridListModule} from '@angular/material/grid-list';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatListModule} from '@angular/material/list';
import {MatMenuModule} from '@angular/material/menu';
import {MatPaginatorModule} from '@angular/material/paginator';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatRadioModule} from '@angular/material/radio';
import {MAT_SELECT_CONFIG, MatSelectModule} from '@angular/material/select';
import {MatSidenavModule} from '@angular/material/sidenav';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatSliderModule} from '@angular/material/slider';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatSortModule} from '@angular/material/sort';
import {MatStepperModule} from '@angular/material/stepper';
import {MatTableModule} from '@angular/material/table';
import {MatTabsModule} from '@angular/material/tabs';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatTooltipModule} from '@angular/material/tooltip';
import {MatBadge} from '@angular/material/badge';

import {AppRoutingModule} from './app-routing.module';
import {CoreModule} from './core/core.module';
import {AppComponent} from './app.component';
// Layout
import {LayoutComponent} from './layout/layout.component';
import {PreloaderDirective} from './layout/preloader.directive';
// Header
import {AppHeaderComponent} from './layout/header/header.component';
// Sidenav
import {AppSidenavComponent} from './layout/sidenav/sidenav.component';
import {ToggleOffcanvasNavDirective} from './layout/sidenav/toggle-offcanvas-nav.directive';
import {AutoCloseMobileNavDirective} from './layout/sidenav/auto-close-mobile-nav.directive';
import {AccordionNavDirective} from './layout/sidenav/accordion-nav.directive';
import {HighlightActiveItemsDirective} from './layout/sidenav/highlight-active-items.directive';
// Footer
import {AppFooterComponent} from './layout/footer/footer.component';

// Sub modules
import {LayoutModule} from './layout/layout.module';

import {NavigationBarConfigurationService} from './cache/uiconfiguration/navigationbarconfiguration.service';
import {CurrentUserService} from './cache/currentuser/currentuser.service';
import {LayoutService} from './layout/layout.service';
import {CacheService} from './cache/cache.service';
import {SchemaService} from './cache/schema/schema.service';

import {DialogConfigurationService} from './cache/uiconfiguration/dialogconfiguration.service';
import {UIConfigurationService} from './cache/uiconfiguration/uiconfiguration.service';
import {Page404Component} from './error-pages/404/404.component';
import {Page500Component} from './error-pages/500/500.component';
import {PageMaintenanceComponent} from './error-pages/maintenance/maintenance.component';
import {LanguageModule} from './language/language.module';
import {SupportedLocaleConfigurationService} from './cache/uiconfiguration/supportedlocaleconfiguration.service';
import {HomePageConfigurationService} from './cache/uiconfiguration/homepageconfiguration.service';
import {GlobalErrorHandler} from './error/GlobalErrorHandler';
import {Page401Component} from './error-pages/401/401.component';
import {ErrorService} from './error/error.service';
import {HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {FilterbuilderModule} from './shared/filterbuilder/filterbuilder.module';
import {ModuleService} from './module/module.service';
import {RequestService} from './cache/request/request.service';
import {TimeAgoPipe} from './shared/component/smd-timeline/time-ago-pipe';
import {TimeAgoModule} from './shared/component/smd-timeline/TimeAgoModule';
import {TimeZoneConfigurationService} from './cache/uiconfiguration/timezoneconfiguration.service';
import {DirectAccessGuard} from './error-pages/directaccessguard';
import {CacheInterceptor} from './resource/cacheinterceptor';
import {TrialExpirationInterceptor} from './core/trial-expiration.interceptor';
import {
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalBroadcastService,
  MsalGuard,
  MsalGuardConfiguration,
  MsalInterceptor,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import {environment} from '../environments/environment';
import {isEntraAuth, resolveApiUrl} from './core/deployment';
import {XmlToJsonConverterService} from './cache/xml-to-json-converter.service';

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function initializeApp(msalService: MsalService): () => Promise<void> {
  return () => {
    return new Promise<void>(resolve => {
      msalService.handleRedirectObservable().subscribe({
        next: () => {
          resolve();
        },
        error: error => {
          console.error('MSAL initialization error:', error);
          resolve(); // Auch bei Fehlern fortfahren
        },
      });
    });
  };
}

export function MSALInstanceFactory(): PublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.clientId, // Ihre Client-ID
      authority: 'https://login.microsoftonline.com/' + environment.msalConfig.tenant, // Ihre Tenant-ID
      redirectUri: window.location.origin, // Redirect URI nach Login
      postLogoutRedirectUri: window.location.origin, // Redirect URI nach Logout
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  // Fügen Sie hier Ihre API-Endpunkte hinzu, die Authentifizierung benötigen
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  // Every call to the Service origin carries the token. ServerApiUrl may be relative ('/api/')
  // when the Service hosts the Portal on the same origin.
  protectedResourceMap.set(resolveApiUrl(environment.ServerApiUrl).origin + '/*', [
    'api://' + environment.msalConfig.serviceClientId + '/access_as_user',
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
    // Entfernt: loginFailedRoute - Fehler werden in der App behandelt
  };
}

@NgModule({
  declarations: [
    AppComponent,
    // Layout
    LayoutComponent,
    PreloaderDirective,
    // Header
    AppHeaderComponent,
    // Sidenav
    AppSidenavComponent,
    ToggleOffcanvasNavDirective,
    AutoCloseMobileNavDirective,
    AccordionNavDirective,
    HighlightActiveItemsDirective,
    // Customizer
    //AppCustomizerComponent,
    // Footer
    AppFooterComponent,
    //Error Pages
    Page404Component,
    Page401Component,
    Page500Component,
    PageMaintenanceComponent,
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
  imports: [
    BrowserModule,
    FormsModule,
    BrowserAnimationsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatCheckboxModule,
    MatChipsModule,
    MatStepperModule,
    MatDatepickerModule,
    MatDialogModule,
    MatExpansionModule,
    MatGridListModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatRadioModule,
    MatRippleModule,
    MatSelectModule,
    MatSidenavModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    MatSortModule,
    MatTableModule,
    MatTabsModule,
    MatToolbarModule,
    MatTooltipModule,
    MatBadge,
    AppRoutingModule,
    // Sub modules
    CoreModule,
    LayoutModule,
    FilterbuilderModule,
    LanguageModule,
    TimeAgoModule,
    MsalModule,
  ],
  providers: [
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    MsalBroadcastService,
    MsalService,
    {provide: HTTP_INTERCEPTORS, useClass: TrialExpirationInterceptor, multi: true},
    isEntraAuth()
      ? [
          {
            provide: HTTP_INTERCEPTORS,
            useClass: MsalInterceptor,
            multi: true,
          },
          {
            provide: MSAL_INTERCEPTOR_CONFIG,
            useFactory: MSALInterceptorConfigFactory,
          },
          {provide: HTTP_INTERCEPTORS, useClass: CacheInterceptor, multi: true},
          MsalGuard,
          {
            provide: ErrorHandler,
            useClass: GlobalErrorHandler,
          },
          {
            provide: MAT_SELECT_CONFIG,
            useValue: {typeaheadDebounceInterval: 500},
          },
        ]
      : [],
    HomePageConfigurationService,
    CurrentUserService,
    RequestService,
    UIConfigurationService,
    NavigationBarConfigurationService,
    DialogConfigurationService,
    TimeZoneConfigurationService,
    XmlToJsonConverterService,
    CacheService,
    SchemaService,
    SupportedLocaleConfigurationService,
    LayoutService,
    ErrorService,
    ModuleService,
    MsalGuard,
    DirectAccessGuard,
    isEntraAuth()
      ? [
          provideAppInitializer(() => {
            const initializerFn = initializeApp(inject(MsalService));
            return initializerFn();
          }),
        ]
      : [],
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor(public appRef: ApplicationRef) {}
}
